import { Actor, Canvas, Engine, KeyEvent, Keys, Random, Scene, vec } from 'excalibur';
import { DriftingDoodle } from '../actors/drifting-doodle';
import { PaperBackground } from '../actors/paper-background';
import { HandLabel } from '../ui/label';
import { SketchButton } from '../ui/sketch-button';
import { paintSpeedButtonMark } from '../ui/speed-button';
import { sfx } from '../utils/audio';
import { DESIGN_WIDTH, SEEDS } from '../utils/constants';
import { isTouchDevice } from '../utils/device';
import { HAND_FONT } from '../utils/fonts';
import { Palette } from '../utils/palette';
import { PlacementRect, randomPointInRects } from '../utils/placement';
import { refreshSceneTheme } from '../themes/theme';
import {
  DOODLE_KINDS,
  css,
  drawHeart,
  drawSparkle,
  paintMiniClock,
  sketchStroke,
  wobblyCirclePath,
  wobblyLinePath,
} from '../utils/sketch';
import { markTutorialSeen } from '../utils/storage';

const ILLUSTRATION_SIZE = 520;
const STEP_COUNT = 3;

interface TutorialStep {
  title: string;
  lines: string[];
}

const TUTORIAL_DOODLE_ZONES: PlacementRect[] = [
  { left: 16, top: 56, right: 84, bottom: 1224 },
  { left: 636, top: 56, right: 704, bottom: 1224 },
  { left: 120, top: 40, right: 600, bottom: 120 },
];

export class TutorialScene extends Scene {
  private step = 0;
  private title!: HandLabel;
  private lineA!: HandLabel;
  private lineB!: HandLabel;
  private lineC!: HandLabel;
  private illustration!: TutorialIllustration;
  private dots!: StepDots;
  private primary!: SketchButton;

  onInitialize(): void {
    this.add(new PaperBackground());
    this.add(
      new HandLabel({
        name: 'tutorial-header',
        text: 'how to play',
        pos: vec(DESIGN_WIDTH / 2, 96),
        size: 36,
        tone: 'inkSoft',
      }),
    );
    this.title = new HandLabel({
      name: 'tutorial-title-text',
      text: '',
      pos: vec(DESIGN_WIDTH / 2, 186),
      size: 62,
      tone: 'ink',
    });
    this.add(this.title);

    this.illustration = new TutorialIllustration();
    this.add(this.illustration);

    this.lineA = new HandLabel({
      name: 'tutorial-line-a',
      text: '',
      pos: vec(DESIGN_WIDTH / 2, 844),
      size: 26,
      tone: 'inkSoft',
    });
    this.lineB = new HandLabel({
      name: 'tutorial-line-b',
      text: '',
      pos: vec(DESIGN_WIDTH / 2, 890),
      size: 26,
      tone: 'inkSoft',
    });
    this.lineC = new HandLabel({
      name: 'tutorial-line-c',
      text: '',
      pos: vec(DESIGN_WIDTH / 2, 936),
      size: 26,
      tone: 'inkSoft',
    });
    this.add(this.lineA);
    this.add(this.lineB);
    this.add(this.lineC);

    this.dots = new StepDots();
    this.add(this.dots);

    this.primary = new SketchButton({
      name: 'tutorial-next',
      text: 'next',
      width: 320,
      height: 88,
      accentTone: 'mint',
      fontSize: 40,
      onPress: this.next,
    });
    this.primary.pos = vec(DESIGN_WIDTH / 2, 1090);
    this.add(this.primary);

    const back = new SketchButton({
      name: 'tutorial-back',
      text: 'back',
      width: 180,
      height: 54,
      accentTone: 'blue',
      fontSize: 28,
      onPress: this.back,
    });
    back.pos = vec(216, 1196);
    this.add(back);

    const skip = new SketchButton({
      name: 'tutorial-skip',
      text: 'skip',
      width: 180,
      height: 54,
      accentTone: 'yellow',
      fontSize: 28,
      onPress: this.skip,
    });
    skip.pos = vec(504, 1196);
    this.add(skip);

    const rng = new Random(SEEDS.tutorial);
    for (let i = 0; i < 6; i++) {
      this.add(
        new DriftingDoodle({
          name: `tutorial-doodle-${i}`,
          kind: DOODLE_KINDS[(i + 5) % DOODLE_KINDS.length],
          pos: randomPointInRects(rng, TUTORIAL_DOODLE_ZONES),
          size: rng.floating(12, 19),
          toneIndex: i,
          seed: SEEDS.tutorial + i * 19,
        }),
      );
    }

    this.applyStep();
  }

  onActivate(): void {
    refreshSceneTheme(this);
    this.input.keyboard.on('press', this.handleKey);
  }

  onDeactivate(): void {
    this.input.keyboard.off('press', this.handleKey);
  }

  onPreUpdate(_engine: Engine, _elapsed: number): void {
    return;
  }

  private steps(): TutorialStep[] {
    return [
      {
        title: 'tap the dots',
        lines: [
          'when a hand reaches its own dot,',
          'tap anywhere. blue is the minute hand,',
          'pink is the hour hand.',
        ],
      },
      {
        title: 'keep it happy',
        lines: [
          'a hit is +100 and adds a little time.',
          'both hands at once is +300.',
          'a miss costs a heart - you have three.',
        ],
      },
      {
        title: 'zoom when you dare',
        lines: isTouchDevice()
          ? [
              'hold the speed button to spin the',
              'hands 1.5x faster. flip it to the',
              'other side with the tiny arrows.',
            ]
          : [
              'hold space to spin the hands 1.5x',
              'faster. faster hands mean faster',
              'points.',
            ],
      },
    ];
  }

  private applyStep(): void {
    const steps = this.steps();
    const current = steps[this.step];
    this.title.text = current.title;
    this.lineA.text = current.lines[0] ?? '';
    this.lineB.text = current.lines[1] ?? '';
    this.lineC.text = current.lines[2] ?? '';
    this.primary.setText(this.step === steps.length - 1 ? "let's play!" : 'next');
    this.illustration.setStep(this.step);
    this.dots.setStep(this.step, STEP_COUNT);
  }

  private next = (): void => {
    sfx.resume();
    if (this.step >= STEP_COUNT - 1) {
      this.finish();
      return;
    }
    this.step += 1;
    this.applyStep();
  };

  private back = (): void => {
    sfx.resume();
    if (this.step === 0) {
      void this.engine.goToScene('title');
      return;
    }
    this.step -= 1;
    this.applyStep();
  };

  private skip = (): void => {
    sfx.resume();
    this.finish();
  };

  private finish(): void {
    markTutorialSeen();
    sfx.start();
    void this.engine.goToScene('game');
  }

  private handleKey = (evt: KeyEvent): void => {
    if (evt.key === Keys.Space || evt.key === Keys.Enter || evt.key === Keys.Right) {
      this.next();
    } else if (evt.key === Keys.Left) {
      this.back();
    } else if (evt.key === Keys.Escape) {
      this.skip();
    }
  };
}

class TutorialIllustration extends Actor {
  private readonly illustrationCanvas: Canvas;
  private step = 0;

  constructor() {
    const canvas = new Canvas({
      width: ILLUSTRATION_SIZE,
      height: ILLUSTRATION_SIZE,
      cache: true,
      draw: (ctx) => paintTutorialIllustration(ctx, this.step),
    });
    super({ name: 'tutorial-illustration', pos: vec(DESIGN_WIDTH / 2, 560), graphic: canvas });
    this.illustrationCanvas = canvas;
  }

  setStep(step: number): void {
    this.step = step;
    this.illustrationCanvas.flagDirty();
  }

  refreshTheme(): void {
    this.illustrationCanvas.flagDirty();
  }
}

class StepDots extends Actor {
  private step = 0;
  private count = STEP_COUNT;
  private readonly dotCanvas: Canvas;

  constructor() {
    const canvas = new Canvas({
      width: 240,
      height: 44,
      cache: true,
      draw: (ctx) => this.paint(ctx),
    });
    super({ name: 'tutorial-dots', pos: vec(DESIGN_WIDTH / 2, 1000), graphic: canvas });
    this.dotCanvas = canvas;
  }

  setStep(step: number, count: number): void {
    this.step = step;
    this.count = count;
    this.dotCanvas.flagDirty();
  }

  private paint(ctx: CanvasRenderingContext2D): void {
    const rng = new Random(SEEDS.tutorial + 7);
    const spacing = 46;
    const startX = 120 - ((this.count - 1) * spacing) / 2;
    for (let i = 0; i < this.count; i++) {
      const active = i === this.step;
      const x = startX + i * spacing;
      ctx.save();
      ctx.fillStyle = css(active ? Palette.blueDeep : Palette.paperEdge, active ? 0.95 : 0.85);
      wobblyCirclePath(ctx, x, 22, active ? 12 : 8, rng, 1.3, 18);
      ctx.fill();
      ctx.restore();
      if (active) {
        sketchStroke(ctx, rng, { color: Palette.ink, width: 2.6, alpha: 0.7 }, (c) =>
          wobblyCirclePath(c, x, 22, 16, rng, 1.4, 20), 1);
      }
    }
  }
}

function paintTutorialIllustration(ctx: CanvasRenderingContext2D, step: number): void {
  const rng = new Random(SEEDS.tutorial + 3);
  const mid = ILLUSTRATION_SIZE / 2;
  ctx.save();
  ctx.translate(mid, mid);

  if (step === 0) {
    paintMiniClock(ctx, rng, {
      radius: 200,
      minuteAngle: 4,
      hourAngle: 208,
      markers: [{ angle: 0, color: Palette.blue }],
      face: 'idle',
    });
    for (let i = 0; i < 3; i++) {
      sketchStroke(ctx, rng, { color: Palette.blueDeep, width: 3.2, alpha: 0.45 - i * 0.12 }, (c) =>
        wobblyCirclePath(c, 0, -196, 26 + i * 15, rng, 2.2, 26), 1);
    }
    drawSparkle(ctx, 44, -226, 18, rng, Palette.yellowDeep, 0.95);
    drawSparkle(ctx, -40, -166, 12, rng, Palette.pinkDeep, 0.8);
  } else if (step === 1) {
    paintMiniClock(ctx, rng, {
      radius: 200,
      minuteAngle: -32,
      hourAngle: 152,
      face: 'happy',
    });
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `64px ${HAND_FONT}`;
    ctx.fillStyle = css(Palette.blueDeep);
    ctx.fillText('+100', -122, -152);
    ctx.font = `52px ${HAND_FONT}`;
    ctx.fillStyle = css(Palette.yellowDeep);
    ctx.fillText('+300', 138, -206);
    ctx.restore();
    drawHeart(ctx, -88, 222, 56, rng, Palette.heart, Palette.heartDeep, 0.95);
    drawHeart(ctx, 0, 222, 56, rng, Palette.heart, Palette.heartDeep, 0.95);
    drawHeart(ctx, 88, 222, 56, rng, Palette.paperEdge, Palette.inkSoft, 0.4);
  } else {
    for (const side of [-1, 1]) {
      for (let i = 0; i < 3; i++) {
        sketchStroke(
          ctx,
          rng,
          { color: side < 0 ? Palette.blueDeep : Palette.pinkDeep, width: 6, alpha: 0.42 - i * 0.1 },
          (c) =>
            wobblyLinePath(
              c,
              side * (152 + i * 8),
              -84 + i * 56,
              side * (218 + i * 12),
              -84 + i * 56,
              rng,
              3,
              6,
            ),
          1,
        );
      }
    }
    paintSpeedButtonMark(ctx, rng, 118, 0.3, 0);
  }

  ctx.restore();
}
